import { useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const upsertMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    Object.entries(attrs).forEach(([k, v]) => {
      if (k !== 'content') el.setAttribute(k, v);
    });
    document.head.appendChild(el);
  }
  el.setAttribute('content', attrs.content || '');
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const SEO_LANGS = ['nl', 'en', 'fr', 'de', 'es', 'zh'];
const DEFAULT_LANG = 'nl';
const OG_LOCALE = { nl: 'nl_BE', en: 'en_GB', fr: 'fr_FR', de: 'de_DE', es: 'es_ES', zh: 'zh_CN' };

/* One URL per language: default language on the bare URL, the others via ?lang= (crawlable, unlike localStorage). */
const langUrl = (origin, path, lang) =>
  `${origin}${path || '/'}${lang === DEFAULT_LANG ? '' : `?lang=${lang}`}`;

const applyHreflang = (origin, path, lang) => {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());   // incl. the ones from the crawler snapshot
  if (!path) return;
  [...SEO_LANGS.map((l) => [l, langUrl(origin, path, l)]), ['x-default', langUrl(origin, path, DEFAULT_LANG)]].forEach(([hl, href]) => {
    const el = document.createElement('link');
    el.setAttribute('rel', 'alternate');
    el.setAttribute('hreflang', hl);
    el.setAttribute('href', href);
    el.setAttribute('data-seo-alt', '1');
    document.head.appendChild(el);
  });
  upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: OG_LOCALE[lang] || OG_LOCALE[DEFAULT_LANG] });
};

const applySeo = ({ title, description, noindex, canonical, ogImage, ogType }) => {
  if (title) document.title = title;
  if (description) {
    upsertMeta('meta[name="description"]', { name: 'description', content: description });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
  }
  if (title) {
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
  }
  upsertMeta('meta[property="og:type"]', { property: 'og:type', content: ogType || 'website' });
  upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'WowProfit.io' });
  upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
  upsertMeta('meta[name="robots"]', {
    name: 'robots',
    content: noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1',
  });
  if (ogImage) upsertMeta('meta[property="og:image"]', { property: 'og:image', content: ogImage });
  if (canonical) {
    upsertLink('canonical', canonical);
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });
  }
};

const JSONLD_ID = 'wowprofit-jsonld';

const applyJsonLd = (data) => {
  const existing = document.getElementById(JSONLD_ID);
  if (existing) existing.remove();
  if (!data) return;
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = JSONLD_ID;
  script.text = JSON.stringify(data);
  document.head.appendChild(script);
};

export const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'WowProfit.io',
  legalName: 'Webtron BV',
  url: 'https://wowprofit.io',
  email: 'info@wowprofit.io',
  description:
    'WowProfit.io is een SaaS-platform dat WooCommerce-webshops hun échte nettowinst laat zien: omzet minus inkoopprijzen, advertentiekosten, verzending en btw.',
  address: {
    '@type': 'PostalAddress',
    addressRegion: 'West-Vlaanderen',
    addressCountry: 'BE',
  },
  vatID: 'BE0793944703',
  areaServed: ['BE', 'NL', 'LU', 'DE', 'FR'],
};

/**
 * Zet title/meta/canonical/JSON-LD. Waarden uit het admin-dashboard (collectie
 * seo_pages) krijgen voorrang op de meegegeven fallbacks.
 */
export const useSeo = ({
  path,
  title,
  description,
  jsonLd = null,
  noindex = false,
  ogImage = null,
  ogType = 'website',
  lang = 'nl',
}) => {
  useEffect(() => {
    let cancelled = false;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const urlLang = new URLSearchParams(window.location.search).get('lang');
    const seoLang = SEO_LANGS.includes(urlLang) ? urlLang : DEFAULT_LANG;   // what a crawler sees for this exact URL
    const canonical = path ? langUrl(origin, path, seoLang) : origin;
    const image = ogImage || `${origin}/og/og-${seoLang}.png`;

    applySeo({ title, description, noindex, canonical, ogImage: image, ogType });
    applyHreflang(origin, path, seoLang);
    applyJsonLd(jsonLd);

    if (path) {
      axios
        .get(`${BACKEND_URL}/api/seo/page`, { params: { path, lang } })
        .then((res) => {
          if (cancelled || !res.data?.found) return;
          applySeo({
            title: res.data.title || title,
            description: res.data.description || description,
            noindex: res.data.noindex || noindex,
            canonical,
            ogImage: res.data.og_image || image,
            ogType,
          });
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
    };
  }, [path, title, description, noindex, lang, JSON.stringify(jsonLd)]);
};

export default useSeo;
